/**
 * The browser's own "install this app" prompt (Chrome, Edge, Android).
 *
 * `beforeinstallprompt` fires once, early in page load, before any React
 * screen exists — so it is caught here, at import time, from `main.tsx`, and
 * held until the install page asks for it. Safari has no such event; there the
 * page shows instructions only.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    for (const listener of listeners) listener()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    for (const listener of listeners) listener()
  })
}

export function canPromptInstall(): boolean {
  return deferred !== null
}

export function onInstallAvailabilityChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Shows the browser's prompt; resolves true when the visitor accepted. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred
  if (event === null) return false
  deferred = null
  await event.prompt()
  const { outcome } = await event.userChoice
  for (const listener of listeners) listener()
  return outcome === 'accepted'
}

/** Already running as the installed app. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}
